import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Search, ExternalLink, Layers, BarChart3, FlaskConical, Database, Lightbulb, FileText } from "lucide-react";

const docs = [
  {
    category: "Getting Started",
    items: [
      { title: "Creating Your First Keyword Landscape", description: "Learn how to set up Hero ASINs and generate your first landscape report.", icon: Layers },
      { title: "Understanding the Dashboard", description: "Overview of key metrics, correlation charts, and quick actions.", icon: BarChart3 },
    ],
  },
  {
    category: "Features",
    items: [
      { title: "Keyword Bank", description: "How to build and manage your master keyword list from multiple sources.", icon: Database },
      { title: "Rank Tracker", description: "Monitor keyword rankings over time.", icon: BarChart3 },
      { title: "Strategy", description: "Turn Content Scorecard gaps into ranked, testable recommendations.", icon: Lightbulb },
      { title: "A/B Tests", description: "Set up intent-driven title experiments and measure performance impact.", icon: FlaskConical },
      { title: "Search Terms", description: "Generate and optimize backend search terms for maximum indexing.", icon: FileText },
    ],
  },
  {
    category: "Advanced",
    items: [
      { title: "Scheduling Landscape Refreshes", description: "Automate recurring landscape generations with flexible scheduling.", icon: Layers },
    ],
  },
];

interface DocumentationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentationModal({ open, onOpenChange }: DocumentationModalProps) {
  const [search, setSearch] = useState("");

  const filtered = docs
    .map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) =>
          item.title.toLowerCase().includes(search.toLowerCase()) ||
          item.description.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((cat) => cat.items.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-base">Documentation</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documentation..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="overflow-y-auto max-h-[55vh] space-y-5 pr-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No results found.</p>
          ) : (
            filtered.map((cat) => (
              <div key={cat.category}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{cat.category}</p>
                <div className="space-y-1">
                  {cat.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.title}
                        className="w-full text-left flex items-start gap-3 p-3 rounded-lg hover:bg-muted transition-colors group"
                      >
                        <Icon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-foreground">{item.title}</p>
                            <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
